import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpContext } from '@angular/common/http';
import { NotificationService, MyNotification } from '@prowodo/angular-client';
import { ADD_BACKEND_AUTH } from '@app/api/auth.tokens';

@Injectable({ providedIn: 'root' })
export class NotificationInboxService {
  private _notifications$ = new BehaviorSubject<MyNotification[]>([]);
  private _showAll$ = new BehaviorSubject<boolean>(false);

  readonly notifications$ = this._notifications$.asObservable();
  readonly showAll$ = this._showAll$.asObservable();

  readonly unreadCount$ = this._notifications$.pipe(
    map(list => list.filter(n => !n.is_read).length),
  );

  private get authCtx() {
    return { context: new HttpContext().set(ADD_BACKEND_AUTH, true) };
  }

  constructor(private notificationService: NotificationService) {}

  load(): void {
    const includeDismissed = this._showAll$.value;
    this.notificationService
      .myNotificationList(
        includeDismissed ? { includeDismissed: true } : {},
        'body', false, this.authCtx
      )
      .subscribe(response => {
        this._notifications$.next(response.results ?? []);
      });
  }

  toggleShowAll(): void {
    this._showAll$.next(!this._showAll$.value);
    this.load();
  }

  markAsRead(id: number): Observable<MyNotification> {
    return this.notificationService
      .myNotificationRead({ id }, 'body', false, this.authCtx)
      .pipe(
        tap(() => {
          const updated = this._notifications$.value.map(n =>
            n.id === id ? { ...n, is_read: true } : n
          );
          this._notifications$.next(updated);
        })
      );
  }

  dismiss(id: number): Observable<MyNotification> {
    return this.notificationService
      .myNotificationDismiss({ id }, 'body', false, this.authCtx)
      .pipe(
        tap(() => {
          if (!this._showAll$.value) {
            const filtered = this._notifications$.value.filter(n => n.id !== id);
            this._notifications$.next(filtered);
          } else {
            const updated = this._notifications$.value.map(n =>
              n.id === id ? { ...n, is_dismiss: true } : n
            );
            this._notifications$.next(updated);
          }
        })
      );
  }
}
